import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  fetchCitingPages,
  fetchSource,
  fetchSources,
  type CitingPage,
  type SourceDetail,
  type SourceKind,
  type SourceSummary,
} from '../api/sources';
import './SourcesPage.css';

const KIND_FILTERS: Array<{ value: SourceKind | ''; label: string }> = [
  { value: '', label: 'all kinds' },
  { value: 'book', label: 'book' },
  { value: 'paper', label: 'paper' },
  { value: 'lecture', label: 'lecture' },
  { value: 'transcript', label: 'transcript' },
  { value: 'url', label: 'url' },
  { value: 'note', label: 'note' },
  { value: 'other', label: 'other' },
];

function formatRelativeTime(ms: number): string {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(ms).toISOString().slice(0, 10);
}

export function SourcesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSlug = searchParams.get('slug');
  const [sources, setSources] = useState<SourceSummary[]>([]);
  const [kind, setKind] = useState<SourceKind | ''>('');
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState<string | null>(initialSlug);
  const [detail, setDetail] = useState<SourceDetail | null>(null);
  const [citingPages, setCitingPages] = useState<CitingPage[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoadingList(true);
    setError(null);
    fetchSources({
      kind: kind || undefined,
      archived: showArchived ? 'all' : 'false',
    })
      .then((r) => {
        setSources(r.sources);
        // If the URL named a slug, prefer it (and clear the param so
        // refresh doesn't re-snap selection). Otherwise pick the
        // first row when nothing is selected yet.
        if (initialSlug && r.sources.some((s) => s.slug === initialSlug)) {
          setSelected(initialSlug);
          searchParams.delete('slug');
          setSearchParams(searchParams, { replace: true });
        } else if (r.sources.length > 0 && !selected) {
          setSelected(r.sources[0]!.slug);
        }
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : String(err)),
      )
      .finally(() => setLoadingList(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, showArchived]);

  useEffect(() => {
    if (!selected) {
      setDetail(null);
      setCitingPages([]);
      return;
    }
    setLoadingDetail(true);
    Promise.all([
      fetchSource(selected),
      fetchCitingPages(selected).catch(() => null),
    ])
      .then(([d, cp]) => {
        setDetail(d);
        setCitingPages(cp ? cp.citing_pages : []);
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : String(err)),
      )
      .finally(() => setLoadingDetail(false));
  }, [selected]);

  return (
    <div className="sources-page">
      <header className="sources-page__header">
        <div>
          <div className="sources-page__title">Sources</div>
          <div className="sources-page__subtitle">
            {sources.length} source{sources.length === 1 ? '' : 's'}
            {showArchived ? ' (incl. archived)' : ''}
          </div>
        </div>
        <button
          type="button"
          className="sources-page__refresh"
          onClick={load}
          disabled={loadingList}
        >
          {loadingList ? 'Loading...' : 'Refresh'}
        </button>
      </header>

      <div className="sources-page__filters">
        <select
          className="sources-page__filter-select"
          value={kind}
          onChange={(e) => setKind(e.target.value as SourceKind | '')}
        >
          {KIND_FILTERS.map((f) => (
            <option key={f.value || 'all'} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        <label className="sources-page__filter-toggle">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          show archived
        </label>
      </div>

      {error && <div className="sources-page__error">{error}</div>}

      <div className="sources-page__split">
        <aside className="sources-page__list">
          {loadingList && sources.length === 0 && (
            <div className="sources-page__status">Loading...</div>
          )}
          {!loadingList && sources.length === 0 && (
            <div className="sources-page__status">No sources yet.</div>
          )}
          {sources.map((s) => (
            <button
              key={s.slug}
              type="button"
              className={
                'sources-page__row' +
                (s.slug === selected ? ' sources-page__row--active' : '') +
                (s.archived ? ' sources-page__row--archived' : '')
              }
              onClick={() => setSelected(s.slug)}
            >
              <div className="sources-page__row-title">{s.title}</div>
              <div className="sources-page__row-meta">
                <span>{s.kind}</span>
                {s.authors.length > 0 && (
                  <>
                    <span>·</span>
                    <span>{s.authors.join(', ')}</span>
                  </>
                )}
                <span>·</span>
                <span>{s.citation_count} cites</span>
                {s.archived && (
                  <>
                    <span>·</span>
                    <span className="sources-page__row-tag">archived</span>
                  </>
                )}
                {s.best_effort && (
                  <>
                    <span>·</span>
                    <span className="sources-page__row-tag">best-effort</span>
                  </>
                )}
              </div>
              <div className="sources-page__row-slug">
                <code>{s.slug}</code>
              </div>
            </button>
          ))}
        </aside>

        <section className="sources-page__detail">
          {!selected && (
            <div className="sources-page__status">Select a source.</div>
          )}
          {selected && loadingDetail && !detail && (
            <div className="sources-page__status">Loading detail...</div>
          )}
          {detail && (
            <>
              <div className="sources-page__detail-head">
                <div className="sources-page__detail-title">
                  {detail.title}
                </div>
                <div className="sources-page__detail-id">
                  <code>{detail.slug}</code>
                </div>
              </div>
              <dl className="sources-page__detail-meta">
                <dt>kind</dt>
                <dd>{detail.kind}</dd>
                {detail.authors.length > 0 && (
                  <>
                    <dt>authors</dt>
                    <dd>{detail.authors.join(', ')}</dd>
                  </>
                )}
                {detail.canonical_ref && (
                  <>
                    <dt>ref</dt>
                    <dd>
                      <code>{detail.canonical_ref}</code>
                    </dd>
                  </>
                )}
                {detail.edition && (
                  <>
                    <dt>edition</dt>
                    <dd>{detail.edition}</dd>
                  </>
                )}
                {detail.source_url && (
                  <>
                    <dt>url</dt>
                    <dd>
                      <a
                        href={detail.source_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {detail.source_url}
                      </a>
                    </dd>
                  </>
                )}
                <dt>added</dt>
                <dd>
                  {formatRelativeTime(detail.added_ms)} (by {detail.added_by_actor})
                </dd>
                <dt>length</dt>
                <dd>{detail.text_length.toLocaleString()} chars</dd>
                <dt>citations</dt>
                <dd>{detail.citation_count} pages</dd>
                {detail.replaces && (
                  <>
                    <dt>replaces</dt>
                    <dd>
                      <code>{detail.replaces}</code>
                    </dd>
                  </>
                )}
              </dl>
              {citingPages.length > 0 && (
                <>
                  <div className="sources-page__detail-text-head">
                    Citing pages ({citingPages.length})
                  </div>
                  <ul className="sources-page__citing-list">
                    {citingPages.map((cp) => (
                      <li
                        key={cp.page_id}
                        className="sources-page__citing-row"
                      >
                        <code>{cp.page_id}</code>
                        <span className="sources-page__citing-meta">
                          {cp.citation_count} cite
                          {cp.citation_count === 1 ? '' : 's'} ·
                          {' '}
                          {formatRelativeTime(cp.last_seen_ms)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <div className="sources-page__detail-text-head">
                Text preview {detail.text_truncated && '(truncated)'}
              </div>
              <pre className="sources-page__detail-text">
                {detail.text_preview}
              </pre>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
